// docsModel.ts - the content model the in-app documentation is written in.
//
// Every docs page is data (a title, a summary and an ordered list of blocks),
// rendered by one template in screens-docs.tsx. Keeping pages as data is what
// makes the sidebar, search, previous/next links and the home page cards all
// derive from a single list instead of being maintained by hand in four places.
import type { ReactNode } from 'react';

export type DocBlock =
  | { kind: 'p'; text: ReactNode }
  | { kind: 'h2'; text: string }
  | { kind: 'h3'; text: string }
  | { kind: 'ul'; items: ReactNode[] }
  | { kind: 'steps'; items: { title: string; body: ReactNode }[] }
  | { kind: 'callout'; text: ReactNode }
  | { kind: 'table'; head: string[]; rows: ReactNode[][] }
  | { kind: 'code'; text: string; caption?: string }
  | { kind: 'image'; placeholder: string; height?: number };

export interface DocPage {
  /** Route path, e.g. `/docs/editor/scene`. */
  path: string;
  /** Sidebar group heading. */
  group: DocGroup;
  /** Short label used in the sidebar and previous/next links. */
  label: string;
  /** Page heading. */
  title: string;
  /** One-sentence summary shown under the title and on cards. */
  summary: string;
  blocks: DocBlock[];
  /** Search keywords not otherwise present in the text. */
  keywords?: string[];
}

export type DocGroup = 'Getting started' | 'The editor' | 'Publishing and players' | 'Account' | 'Reference';

export const DOC_GROUPS: DocGroup[] = ['Getting started', 'The editor', 'Publishing and players', 'Account', 'Reference'];

/** Flatten a block's ReactNode text to a plain string for search. */
export function nodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join(' ');
  if (typeof node === 'object' && 'props' in node) {
    const props = (node as { props?: { children?: ReactNode } }).props;
    return nodeText(props?.children);
  }
  return '';
}

export function blockText(block: DocBlock): string {
  switch (block.kind) {
    case 'p':
    case 'callout':
      return nodeText(block.text);
    case 'h2':
    case 'h3':
      return block.text;
    case 'ul':
      return block.items.map(nodeText).join(' ');
    case 'steps':
      return block.items.map((s) => s.title + ' ' + nodeText(s.body)).join(' ');
    case 'table':
      return block.head.join(' ') + ' ' + block.rows.map((r) => r.map(nodeText).join(' ')).join(' ');
    case 'code':
      return block.text + ' ' + (block.caption ?? '');
    case 'image':
      return '';
  }
}

/** The readable text of a page: title, summary and body, in reading order. */
export function pageText(page: DocPage): string {
  return [page.title, page.summary, ...page.blocks.map(blockText)].join(' ');
}

export interface DocSearchHit {
  page: DocPage;
  /** A short excerpt around the first match, for the results list. */
  snippet: string;
}

/**
 * Full-text search across every page. Every whitespace-separated term must
 * appear somewhere in the page (title, summary, keywords or body). Pages
 * whose title matches rank first.
 */
export function searchDocs(pages: DocPage[], query: string): DocSearchHit[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  const hits: { hit: DocSearchHit; score: number }[] = [];
  for (const page of pages) {
    const body = pageText(page);
    const lower = body.toLowerCase();
    const searchable = lower + ' ' + (page.keywords ?? []).join(' ').toLowerCase();
    if (!terms.every((t) => searchable.includes(t))) continue;
    const title = page.title.toLowerCase();
    const score = terms.reduce((acc, t) => acc + (title.includes(t) ? 3 : 0) + (page.summary.toLowerCase().includes(t) ? 1 : 0), 0);
    // Snippet from the readable text; a keyword-only match falls back to the summary.
    const first = terms.find((t) => lower.includes(t)) ?? '';
    const at = first ? lower.indexOf(first) : 0;
    const start = Math.max(0, at - 50);
    const raw = body.slice(start, Math.min(body.length, at + 90)).replace(/\s+/g, ' ').trim();
    const snippet = (start > 0 ? '…' : '') + raw + (at + 90 < body.length ? '…' : '');
    hits.push({ hit: { page, snippet }, score });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.map((h) => h.hit);
}
